import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { stepLabel } from '../content/contentStudioSnapshot';

// Tracks the Content Studio "window": whether it's open, where to restore to,
// where to return when minimized/closed, and max vs windowed mode. Persisted so
// the minimized taskbar chip survives reloads.

const KEY = 'content-window';
const DEFAULT = { open: false, lastPath: '/content/ingestion', returnPath: '/dashboard', mode: 'max' };

/** True only for Content Studio routes — not `/content-generation-requests` or `/content-package/…`. */
function isContentStudioPath(pathname = '') {
  return pathname === '/content' || pathname.startsWith('/content/');
}

function normalizeLastPath(path) {
  const base = String(path || '').split('?')[0];
  if (!isContentStudioPath(base)) return '/content/ingestion';
  if (base.startsWith('/content/registry')) return '/content/ingestion';
  if (base === '/content/requests') return '/content/cockpit';
  return path || '/content/ingestion';
}

function load() {
  try {
    const s = { ...DEFAULT, ...JSON.parse(localStorage.getItem(KEY) || '{}') };
    if (s.lastPath) s.lastPath = normalizeLastPath(s.lastPath);
    return s;
  } catch { return { ...DEFAULT }; }
}

const ContentWindowContext = createContext(null);

export function ContentWindowProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState(load);
  const prevPath = useRef(location.pathname);
  const stateRef = useRef(state);
  stateRef.current = state;

  // Observe navigation: opening/closing the studio + remembering paths.
  useEffect(() => {
    const full = location.pathname + location.search;
    const onContent = isContentStudioPath(location.pathname);
    setState((s) => {
      if (!onContent) return s;
      const cameFromMain = !isContentStudioPath(prevPath.current);
      return {
        ...s,
        open: true,
        lastPath: full,
        returnPath: cameFromMain ? prevPath.current : s.returnPath,
      };
    });
    prevPath.current = location.pathname;
  }, [location]);

  useEffect(() => { try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* ignore */ } }, [state]);

  const minimize = useCallback(() => {
    const full = location.pathname + location.search;
    const returnPath = stateRef.current.returnPath || '/dashboard';
    if (isContentStudioPath(location.pathname)) {
      setState((s) => ({ ...s, open: true, lastPath: full }));
    }
    navigate(returnPath);
  }, [location, navigate]);

  const close = useCallback(() => {
    setState((s) => ({ ...s, open: false }));
    navigate(stateRef.current.returnPath || '/dashboard');
  }, [navigate]);

  const restore = useCallback(() => {
    navigate(normalizeLastPath(stateRef.current.lastPath));
  }, [navigate]);

  const openStudio = useCallback(() => {
    const path = normalizeLastPath(stateRef.current.lastPath);
    setState((s) => ({ ...s, open: true }));
    navigate(path);
  }, [navigate]);

  const toggleMode = useCallback(() => {
    setState((s) => ({ ...s, mode: s.mode === 'max' ? 'window' : 'max' }));
  }, []);

  const value = {
    ...state,
    stepLabel: stepLabel(state.lastPath),
    onContent: isContentStudioPath(location.pathname),
    minimize,
    close,
    restore,
    openStudio,
    toggleMode,
  };

  return <ContentWindowContext.Provider value={value}>{children}</ContentWindowContext.Provider>;
}

export function useContentWindow() {
  return useContext(ContentWindowContext) || {
    open: false,
    onContent: false,
    mode: 'max',
    lastPath: '/content/ingestion',
    stepLabel: 'Content Studio',
    minimize() {},
    close() {},
    restore() {},
    openStudio() {},
    toggleMode() {},
  };
}
